/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';

import React from 'react';

import { toast } from 'react-toastify';

import {
  ListeQueryContext,
  ListeTable,
  ListeTableContainer,
  type ListeType,
} from '@mairistem/liste';

import type { SituationScolaire } from '@/types';

import { request } from '@mairistem/tools';
import SituationScolaireListeTableRow from './SituationScolaireListeTableRow';

interface Props {
  /** Nombre de lignes */
  count?: number;
  /** Données de la liste */
  items?: SituationScolaire[];
  /** État du chargement */
  loading?: boolean;
  /** Callback appelé lors du chargement de la liste */
  onLoad?: () => void;
  /** Callback appelé lors de la sélection d'un item */
  onSelect?: (
    qualification: SituationScolaire,
    qualifications: SituationScolaire[],
  ) => void;
  /** Nombre de lignes par page */
  perPage?: number;
  /** Callback appelé lors d'un filtrage sur la liste */
  onSort?: () => void;
  /** Callback appelé lors de la sauvegarde */
  onSave?: (qualification: SituationScolaire) => void;
  /** Situation scolaire sélectionnée */
  selectedSituationScolaire?: SituationScolaire;
  /** Callback appelé lors de la réinitialisation des lignes */
  resetSelectedItems?: (qualification: SituationScolaire) => void;
}

const SituationScolaireListeTable = (props: Props) => {
  const {
    count = 0,
    perPage = 50,
    items = [],
    selectedSituationScolaire = {},
    loading = false,
    onLoad = null,
    onSelect = null,
    onSort = null,
    onSave = null,
    resetSelectedItems = null,
  } = props;

  const [errors, setErrors] = React.useState([]);

  const { addItem, removeItem, updateItem } =
    React.useContext(ListeQueryContext);

  const deleteSituationsScolaires = React.useCallback(
    (item) => {
      request
        .delete(
          `/api/administres/recensement-citoyen/situations-scolaires/${item.identifiant}`,
        )
        .then(() => {
          removeItem(item);
        })
        .catch(() => {
          toast(
            'Suppression impossible, situation scolaire liée à un recensé.',
            { type: 'error' },
          );
        });
    },
    [removeItem],
  );

  const postSituationsScolaires = React.useCallback(
    (item) => {
      request
        .post('/api/administres/recensement-citoyen/situations-scolaires', item)
        .then((i: ListeType) => {
          addItem(i);
        });
    },
    [addItem],
  );

  const putSituationsScolaires = React.useCallback(
    (item) => {
      request
        .put(
          `/api/administres/recensement-citoyen/situations-scolaires/${item.identifiant}`,
          item,
        )
        .then((i: ListeType) => {
          updateItem(i);
        });
    },
    [updateItem],
  );

  const handleDoubleClick = React.useCallback(
    (situationScolaire: SituationScolaire) => {
      if (onSelect) onSelect(situationScolaire, items);
      if (onSave) onSave(situationScolaire);
    },
    [items, onSave, onSelect],
  );

  const handleSaveSituationScolaire = React.useCallback(
    async (item: SituationScolaire, status: string) => {
      if (_.isEmpty(item?.libelle) || _.isEmpty(item?.code)) {
        setErrors(
          _.filter(
            [
              _.isEmpty(item?.libelle)
                ? {
                    field: 'libelle',
                    itemId: item?.identifiant,
                    message: 'Le libelle ne doit pas être vide',
                  }
                : null,
              _.isEmpty(item?.code)
                ? {
                    field: 'code',
                    itemId: item?.identifiant,
                    message: 'Le code ne doit pas être vide',
                  }
                : null,
            ],
            (situationScolaire) => !_.isNil(situationScolaire),
          ),
        );

        return false;
      }

      setErrors(null);

      if (status === 'creating') {
        try {
          await postSituationsScolaires(item);
          return true;
        } catch {
          setErrors([
            {
              itemId: item?.identifiant,
              message: 'Les données doivent être uniques',
            },
          ]);
          return false;
        }
      } else {
        if (item?.identifiant === selectedSituationScolaire?.identifiant) {
          onSave(item);
          resetSelectedItems(item);
        }
        return putSituationsScolaires(item);
      }
    },
    [
      onSave,
      selectedSituationScolaire,
      resetSelectedItems,
      postSituationsScolaires,
      putSituationsScolaires,
    ],
  );

  const handleAddSituationScolaire = React.useCallback(
    (newItem) => ({
      ...newItem,
      libelle: '',
      code: '',
      ordre: 1, // On ajoute l'item en début de liste
    }),
    [],
  );

  const handleDeleteSituationScolaire = React.useCallback(
    (item) => {
      if (_.isEqual(item, selectedSituationScolaire)) {
        onSave({});
        resetSelectedItems({});
      }

      deleteSituationsScolaires(item);
    },
    [
      selectedSituationScolaire,
      deleteSituationsScolaires,
      onSave,
      resetSelectedItems,
    ],
  );

  const handleCancelClick = React.useCallback(() => {
    setErrors([]);
  }, []);

  const renderRow = (
    situationScolaire: SituationScolaire,
    _invalidate: any,
    rowProps: any,
    utilities: any,
  ) => (
    <SituationScolaireListeTableRow
      situationScolaire={situationScolaire}
      rowProps={rowProps}
      utilities={utilities}
      errors={errors}
      onDoubleClick={handleDoubleClick}
    />
  );

  return (
    <ListeTableContainer
      onAddClick={handleAddSituationScolaire}
      onValidateClick={handleSaveSituationScolaire}
      onCancelClick={handleCancelClick}
      onDeleteClick={handleDeleteSituationScolaire}
      additionLabel="Ajouter une nouvelle situation scolaire"
      items={items}
      errors={errors}
      switchToCreationIfEmpty
      allowAdditions
      allowModifications
      allowDeletions
    >
      <ListeTable
        count={count}
        loading={loading}
        onLoad={onLoad}
        onSelect={onSelect}
        perPage={perPage}
        onSort={onSort}
        selection="click"
        selectedItems={
          _.isEmpty(selectedSituationScolaire)
            ? []
            : [selectedSituationScolaire]
        }
        showCount
        countLabel={count > 1 ? 'situations scolaires' : 'situation scolaire'}
      >
        <ListeTable.Header>
          <ListeTable.Column
            name="code"
            sortable
            width={2}
            only={['m', 'l', 'xl']}
            required
          >
            Code
          </ListeTable.Column>
          <ListeTable.Column
            name="libelle"
            sortable
            width={14}
            only={['s', 'm', 'l', 'xl']}
            required
          >
            Libellé
          </ListeTable.Column>
        </ListeTable.Header>
        {renderRow as unknown as React.ReactNode}
      </ListeTable>
    </ListeTableContainer>
  );
};

export default SituationScolaireListeTable;
